import type { Held, UsageDay, UsageGroup } from '../lib/usage'
import { heldText, pctOf } from '../lib/usage'

/**
 * 301. The pieces of #/admin/usage that draw numbers - separate from the route
 * so the suite can render them (the route imports the shell, which the test
 * bundler cannot mount; lib/adminHome.ts says why).
 *
 * Every count here is company-wide or held back by the server (null = "fewer
 * than k"). Nothing in this file can name anyone: it receives no person.
 *
 * Charts follow AskOneChart's grammar (one scale for marks, ticks and labels;
 * the top tick is the smallest multiple of 5 at or above the peak), so the two
 * analytics pages read alike. Text is in text tokens; colour only on marks.
 */

const nf = new Intl.NumberFormat('en-CA')
export const fmt = (n: number) => nf.format(n)

export function dayLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/* ── stacked bars by day ─────────────────────────────────────────────────── */

export type DaySeries = { key: string; label: string; cls: string; values: (number | null)[] }

export function DayBars({
  days,
  series,
  label,
  missing,
}: {
  days: readonly string[]
  series: readonly DaySeries[]
  label: string
  /** Shown under the chart when a day has no figure (null), e.g. before the snapshot ran. */
  missing?: string
}) {
  const W = 760
  const H = 180
  const L = 34
  const R = 8
  const T = 14
  const B = 26
  const totals = days.map((_, i) => series.reduce((s, x) => s + (x.values[i] ?? 0), 0))
  const peak = Math.max(1, ...totals)
  const top = Math.max(5, Math.ceil(peak / 5) * 5)
  const ticks = [0, 1, 2, 3, 4, 5].map((i) => (top * i) / 5).filter((v) => Number.isInteger(v))
  const cw = (W - L - R) / Math.max(1, days.length)
  const bw = Math.min(cw * 0.62, 26)
  const y = (v: number) => T + (H - T - B) * (1 - v / top)
  const every = days.length > 45 ? 14 : days.length > 10 ? 4 : 1
  const anyMissing = series.some((s) => s.values.some((v) => v === null))
  return (
    <div className="askone__chartwrap">
      <svg className="askone__chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
        {ticks.map((v) => (
          <g key={v}>
            <line className="askone__grid" x1={L} x2={W - R} y1={y(v)} y2={y(v)} />
            <text className="askone__tick" x={L - 6} y={y(v) + 3} textAnchor="end">{fmt(v)}</text>
          </g>
        ))}
        {days.map((day, i) => {
          const cx = L + cw * i + cw / 2
          const x = cx - bw / 2
          let base = 0
          const total = totals[i]
          const nothing = series.every((s) => s.values[i] === null)
          return (
            <g key={day}>
              <title>{`${dayLabel(day)}: ${series.map((s) => `${s.label} ${s.values[i] === null ? 'no figure' : fmt(s.values[i] as number)}`).join(', ')}`}</title>
              {/* a hit target the full height of the column, so the tooltip is easy to reach */}
              <rect className="usage__hit" x={L + cw * i} y={T} width={cw} height={H - T - B} />
              {nothing ? (
                <line className="usage__nofigure" x1={x} x2={x + bw} y1={y(0) - 1} y2={y(0) - 1} />
              ) : (
                series.map((s) => {
                  const n = s.values[i] ?? 0
                  if (!n) return null
                  const y1 = y(base + n)
                  const y0 = y(base)
                  // 2px surface gap where a segment sits on another.
                  const gap = base > 0 ? 2 : 0
                  base += n
                  const h = Math.max(1, y0 - y1 - gap)
                  return <rect key={s.key} className={`usage__bar ${s.cls}`} x={x} y={y1} width={bw} height={h} rx={2} />
                })
              )}
              {/* Selective labels: every bar only when there are few; the ticks and the tooltip carry the rest. */}
              {total > 0 && days.length <= 14 && (
                <text className="askone__barn" x={cx} y={y(total) - 4} textAnchor="middle">{fmt(total)}</text>
              )}
              {((i % every === 0 && days.length - 1 - i >= every / 2) || i === days.length - 1) && (
                <text className="askone__tick" x={cx} y={H - 8} textAnchor="middle">{dayLabel(day)}</text>
              )}
            </g>
          )
        })}
        <line className="askone__axis" x1={L} x2={W - R} y1={y(0)} y2={y(0)} />
      </svg>
      {series.length > 1 && (
        <p className="askone__legend">
          {series.map((s) => (
            <span key={s.key}><i className={`askone__sw usage__sw ${s.cls}`} aria-hidden="true" />{s.label}</span>
          ))}
        </p>
      )}
      {anyMissing && missing && <p className="askone__note">{missing}</p>}
    </div>
  )
}

/** The trend rows as the three day series the page draws. */
export function trendSeries(trend: readonly UsageDay[]) {
  return {
    days: trend.map((t) => t.day),
    active: [{ key: 'active', label: 'People active', cls: 'usage__bar--a', values: trend.map((t) => t.active) }],
    signIns: [
      { key: 'first', label: 'First sign-in', cls: 'usage__bar--a', values: trend.map((t) => t.first_sign_ins) },
      { key: 'again', label: 'Signed in again', cls: 'usage__bar--b', values: trend.map((t) => t.sign_ins - t.first_sign_ins) },
    ],
    devices: [
      { key: 'app', label: 'Installed app', cls: 'usage__bar--a', values: trend.map((t) => t.app_visits) },
      { key: 'tab', label: 'Browser tab', cls: 'usage__bar--b', values: trend.map((t) => t.visits - t.app_visits) },
    ],
  }
}

/* ── 24 hours ────────────────────────────────────────────────────────────── */

export function HourBars({ rows, label }: { rows: readonly { hour: number; n: number }[]; label: string }) {
  const W = 760
  const H = 150
  const L = 34
  const R = 8
  const T = 14
  const B = 24
  const peak = Math.max(1, ...rows.map((r) => r.n))
  const top = Math.max(5, Math.ceil(peak / 5) * 5)
  const cw = (W - L - R) / 24
  const bw = Math.min(cw * 0.62, 20)
  const y = (v: number) => T + (H - T - B) * (1 - v / top)
  return (
    <div className="askone__chartwrap">
      <svg className="askone__chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
        {[0, top / 2, top].filter((v) => Number.isInteger(v)).map((v) => (
          <g key={v}>
            <line className="askone__grid" x1={L} x2={W - R} y1={y(v)} y2={y(v)} />
            <text className="askone__tick" x={L - 6} y={y(v) + 3} textAnchor="end">{fmt(v)}</text>
          </g>
        ))}
        {rows.map((r) => {
          const cx = L + cw * r.hour + cw / 2
          return (
            <g key={r.hour}>
              <title>{`${String(r.hour).padStart(2, '0')}:00 to ${String((r.hour + 1) % 24).padStart(2, '0')}:00 - ${fmt(r.n)}`}</title>
              <rect className="usage__hit" x={L + cw * r.hour} y={T} width={cw} height={H - T - B} />
              {r.n > 0 && <rect className="usage__bar usage__bar--a" x={cx - bw / 2} y={y(r.n)} width={bw} height={y(0) - y(r.n)} rx={2} />}
              {r.hour % 3 === 0 && (
                <text className="askone__tick" x={cx} y={H - 8} textAnchor="middle">{String(r.hour).padStart(2, '0')}</text>
              )}
            </g>
          )
        })}
        <line className="askone__axis" x1={L} x2={W - R} y1={y(0)} y2={y(0)} />
      </svg>
    </div>
  )
}

/* ── a list of labelled bars ─────────────────────────────────────────────── */

export type BarRow = { key: string; label: string; n: Held; sub?: string }

export function BarList({ rows, k, label, empty }: { rows: readonly BarRow[]; k: number; label: string; empty: string }) {
  if (rows.length === 0) return <p className="askone__empty">{empty}</p>
  const max = Math.max(1, ...rows.map((r) => r.n ?? 0))
  return (
    <ul className="usage__bars" aria-label={label}>
      {rows.map((r) => (
        <li key={r.key} className="usage__barrow">
          <span className="usage__barlabel">{r.label}</span>
          <span className="usage__barval">
            {heldText(r.n, k, fmt)}
            {r.sub && <span className="usage__barsub"> {r.sub}</span>}
          </span>
          <span className="usage__track" aria-hidden="true">
            {r.n !== null && r.n > 0 && <b className="usage__fill" style={{ inlineSize: `${(r.n * 100) / max}%` }} />}
          </span>
        </li>
      ))}
    </ul>
  )
}

/* ── the launch funnel ───────────────────────────────────────────────────── */

export function Funnel({ steps }: { steps: readonly { label: string; n: number; hint: string }[] }) {
  const first = Math.max(1, steps[0]?.n ?? 1)
  return (
    <ol className="usage__funnel">
      {steps.map((s, i) => (
        <li key={s.label} className="usage__step">
          <span className="usage__steplabel">{s.label}</span>
          <span className="usage__stepval">
            {fmt(s.n)}
            {i > 0 && <span className="usage__barsub"> {pctOf(s.n, steps[0].n)}</span>}
          </span>
          {/* Its own line, so a long hint never squeezes the label on a phone. */}
          <span className="usage__stephint">{s.hint}</span>
          <span className="usage__track" aria-hidden="true">
            <b className="usage__fill" style={{ inlineSize: `${(s.n * 100) / first}%` }} />
          </span>
        </li>
      ))}
    </ol>
  )
}

/* ── by country / by company, as a table (the table view of the funnel) ──── */

export function GroupTable({ rows, k, first }: { rows: readonly UsageGroup[]; k: number; first: string }) {
  const cell = (n: Held, of: Held) => (
    <>
      {heldText(n, k, fmt)}
      {n !== null && of !== null && of > 0 && <span className="usage__barsub"> {pctOf(n, of)}</span>}
    </>
  )
  return (
    <div className="usage__tablewrap">
      <table className="usage__table">
        <thead>
          <tr>
            <th scope="col">{first}</th>
            <th scope="col">Staff</th>
            <th scope="col">Signed in</th>
            <th scope="col">Active this week</th>
            <th scope="col">Added a photo</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.group ?? '(small)'}>
              <th scope="row">{r.group ?? `Groups of fewer than ${k}, together`}</th>
              <td>{fmt(r.staff)}</td>
              <td>{cell(r.signed_in, r.staff)}</td>
              <td>{cell(r.active_7d, r.signed_in)}</td>
              <td>{cell(r.with_photo, r.signed_in)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
